import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";

const FADE_PX = 10;
const EDGE_CLEAR_PX = 8;

function fadeStyle(axis: "x" | "y", canBack: boolean, canForward: boolean): CSSProperties {
  if (!canBack && !canForward) return {};
  const back = canBack ? `transparent 0, transparent ${EDGE_CLEAR_PX}px, black ${EDGE_CLEAR_PX + FADE_PX}px` : "black 0";
  const forward = canForward
    ? `black calc(100% - ${EDGE_CLEAR_PX + FADE_PX}px), transparent calc(100% - ${EDGE_CLEAR_PX}px), transparent 100%`
    : "black 100%";
  const maskImage = `linear-gradient(to ${axis === "x" ? "right" : "bottom"}, ${back}, ${forward})`;
  return { maskImage, WebkitMaskImage: maskImage };
}

/** Pointer drag scrolling for mobile selector strips.
 * Native overflow can lose the gesture when it starts on a button; this keeps
 * the scroll owned by the row/rail and suppresses the post-drag click. */
export function useHorizontalDragScroll(axis: "x" | "y" = "x") {
  const ref = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ id: number; pos: number; scroll: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [fade, setFade] = useState({ back: false, forward: false });

  const updateFade = useCallback((el: HTMLElement | null = ref.current) => {
    if (!el) return;
    const scroll = axis === "x" ? el.scrollLeft : el.scrollTop;
    const max = Math.max(0, (axis === "x" ? el.scrollWidth - el.clientWidth : el.scrollHeight - el.clientHeight));
    const back = scroll > 1;
    const forward = scroll < max - 1;
    setFade((prev) => (prev.back === back && prev.forward === forward ? prev : { back, forward }));
  }, [axis]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    updateFade(el);
    const raf = requestAnimationFrame(() => updateFade(el));
    const ro = new ResizeObserver(() => updateFade(el));
    const mo = new MutationObserver(() => updateFade(el));
    ro.observe(el);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
    };
  }, [updateFade]);

  useEffect(() => () => cleanupRef.current?.(), []);

  return {
    ref,
    fadeStyle: fadeStyle(axis, fade.back, fade.forward),
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const el = e.currentTarget;
      drag.current = {
        id: e.pointerId,
        pos: axis === "x" ? e.clientX : e.clientY,
        scroll: axis === "x" ? el.scrollLeft : el.scrollTop,
        moved: false,
      };

      const onMove = (ev: PointerEvent) => {
        const d = drag.current;
        if (!d || d.id !== ev.pointerId) return;
        const delta = (axis === "x" ? ev.clientX : ev.clientY) - d.pos;
        if (Math.abs(delta) > 8) d.moved = true;
        if (!d.moved) return;
        ev.preventDefault();
        if (axis === "x") el.scrollLeft = d.scroll - delta;
        else el.scrollTop = d.scroll - delta;
        updateFade(el);
      };
      const onEnd = (ev: PointerEvent) => {
        const d = drag.current;
        if (d?.id === ev.pointerId && d.moved) suppressClick.current = true;
        drag.current = null;
        cleanupRef.current?.();
      };
      cleanupRef.current?.();
      cleanupRef.current = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onEnd);
        window.removeEventListener("pointercancel", onEnd);
        cleanupRef.current = null;
      };
      window.addEventListener("pointermove", onMove, { passive: false });
      window.addEventListener("pointerup", onEnd);
      window.addEventListener("pointercancel", onEnd);
    },
    onScroll: (e: React.UIEvent<HTMLElement>) => updateFade(e.currentTarget),
    onClickCapture: (e: React.MouseEvent<HTMLElement>) => {
      if (!suppressClick.current) return;
      suppressClick.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
  };
}
